/**
 * ISO 9660 reader over a CD track, streaming.
 *
 * The design mirrors `zip/read.ts`: the image arrives as a `File`, which gives
 * random access without materialising it, so we read the volume descriptors,
 * walk the directory tree, and then slice each file's extent on demand. A 668MB
 * disc costs us the directory tree (tens of KB) plus whatever extent is being
 * copied right now.
 *
 * The one place this is harder than a zip: on a raw MODE1/2352 image the user
 * bytes are *not contiguous*, so an extent cannot be one `Blob.slice`. Those are
 * rebuilt a chunk at a time, lifting 2048 bytes out of every 2352-byte frame.
 * Cooked images take the slice fast path and cost nothing at all.
 *
 * ## Why not `Response.blob()`
 *
 * Draining a `ReadableStream` through `new Response(stream).blob()` is the tidy
 * way to write this and it does not work: Chrome buffers that body in memory and
 * gives up somewhere between 64MB and 256MB, throwing a bare `TypeError: Failed
 * to fetch` — which reads like a network error and is nothing of the kind.
 * master.dat is 318MB, so every disc import failed on it.
 *
 * The sink below instead turns each chunk into its own small `Blob` as it is
 * produced. That hands the bytes to the browser's blob store immediately, so the
 * JS heap only ever holds one chunk and the array holds references; the final
 * `new Blob(parts)` is a cheap concatenation of things already out of memory.
 * Verified at 333MB, including the round-trip through IndexedDB.
 */

import { USER_SIZE, isCooked, type SectorGeometry } from './cue';

export class IsoReadError extends Error {}

export interface IsoTrack {
  /** The .bin/.iso file. */
  blob: Blob;
  /** Byte offset in `blob` of this track's sector 0. */
  base: number;
  geometry: SectorGeometry;
}

export interface IsoEntry {
  /** Full path from the disc root, `/`-joined, never leading-slash. */
  path: string;
  size: number;
  isDirectory: boolean;
  /** Sector index of the file's first data sector, relative to the track. */
  lba: number;
  /** Set when the file is split across several directory records. */
  extents?: { lba: number; size: number }[];
}

export interface IsoVolume {
  /** Volume identifier from the descriptor we used, trimmed. */
  label: string;
  /** Total sectors the volume claims to occupy. */
  volumeSectors: number;
  /** True when names came from a Joliet supplementary descriptor. */
  joliet: boolean;
  entries: IsoEntry[];
}

/** Directory records may not straddle a logical sector; a zero length means "pad to the next one". */
const SECTOR_ALIGN = USER_SIZE;

/** Bigger reads amortise the per-slice overhead; 512 sectors is ~1.15MB raw. */
const STREAM_SECTORS = 512;

function byteOffset(track: IsoTrack, lba: number): number {
  return track.base + lba * track.geometry.rawSize;
}

/** Reads `count` sectors of user data. Only used for descriptors and directories. */
export async function readSectors(track: IsoTrack, lba: number, count: number): Promise<Uint8Array> {
  const { geometry } = track;
  const start = byteOffset(track, lba);
  const end = start + count * geometry.rawSize;
  if (start < 0 || start >= track.blob.size) {
    throw new IsoReadError(`Sector ${lba} is past the end of the image.`);
  }
  const raw = new Uint8Array(await track.blob.slice(start, Math.min(end, track.blob.size)).arrayBuffer());
  if (isCooked(geometry)) return raw;
  const whole = Math.floor(raw.length / geometry.rawSize);
  const out = new Uint8Array(whole * USER_SIZE);
  for (let i = 0; i < whole; i++) {
    const from = i * geometry.rawSize + geometry.headerOffset;
    out.set(raw.subarray(from, from + USER_SIZE), i * USER_SIZE);
  }
  return out;
}

/**
 * Probes the image for a valid `CD001` volume descriptor at sector 16 under a
 * given geometry. This is what lets a lone .bin be imported without a cue, and
 * what lets us catch a cue that lies about its track mode — both of which
 * happen often enough that trusting the declared mode is a bug, not a shortcut.
 */
export async function geometryFits(blob: Blob, base: number, geometry: SectorGeometry): Promise<boolean> {
  const start = base + 16 * geometry.rawSize + geometry.headerOffset;
  if (start + 6 > blob.size) return false;
  const d = new Uint8Array(await blob.slice(start, start + 7).arrayBuffer());
  return d[1] === 0x43 && d[2] === 0x44 && d[3] === 0x30 && d[4] === 0x30 && d[5] === 0x31; // "CD001"
}

// --- volume descriptors ------------------------------------------------------

const VD_PRIMARY = 1;
const VD_SUPPLEMENTARY = 2;
const VD_TERMINATOR = 255;

/** Joliet announces itself with one of three ISO 2022 escape sequences. */
function isJolietEscape(esc: Uint8Array): boolean {
  for (let i = 0; i + 2 < esc.length; i++) {
    if (esc[i] === 0x25 && esc[i + 1] === 0x2f && (esc[i + 2] === 0x40 || esc[i + 2] === 0x43 || esc[i + 2] === 0x45)) {
      return true;
    }
  }
  return false;
}

interface Descriptor {
  type: number;
  raw: Uint8Array;
  joliet: boolean;
}

async function readDescriptors(track: IsoTrack): Promise<Descriptor[]> {
  const out: Descriptor[] = [];
  // The descriptor set starts at 16 and is terminated by a type-255 record.
  // Capping the scan stops a corrupt image from walking the whole disc.
  for (let lba = 16; lba < 16 + 64; lba++) {
    let sector: Uint8Array;
    try {
      sector = await readSectors(track, lba, 1);
    } catch {
      break;
    }
    if (sector.length < USER_SIZE) break;
    const id = String.fromCharCode(sector[1], sector[2], sector[3], sector[4], sector[5]);
    if (id !== 'CD001') break;
    const type = sector[0];
    if (type === VD_TERMINATOR) break;
    out.push({ type, raw: sector, joliet: type === VD_SUPPLEMENTARY && isJolietEscape(sector.subarray(88, 120)) });
  }
  if (!out.length) throw new IsoReadError('No ISO 9660 volume descriptor found at sector 16.');
  return out;
}

// --- directory records -------------------------------------------------------

const FLAG_DIRECTORY = 0x02;
const FLAG_MULTI_EXTENT = 0x80;

interface RawRecord {
  lba: number;
  size: number;
  flags: number;
  name: string;
}

/**
 * ISO names are `NAME.EXT;1`; Joliet names are the real thing in UTF-16BE.
 *
 * The version suffix is stripped when present — and it is genuinely optional in
 * the wild: the retail Fallout 2 disc omits `;1` entirely, so a reader that
 * *requires* it to split the name finds nothing to strip and must not care.
 */
function decodeName(bytes: Uint8Array, joliet: boolean): string {
  let name: string;
  if (joliet) {
    let s = '';
    for (let i = 0; i + 1 < bytes.length; i += 2) s += String.fromCharCode((bytes[i] << 8) | bytes[i + 1]);
    name = s;
  } else {
    name = String.fromCharCode(...bytes);
  }
  const semi = name.lastIndexOf(';');
  if (semi >= 0) name = name.slice(0, semi);
  // "FOO." is how a level-1 name with no extension is padded out.
  if (name.endsWith('.')) name = name.slice(0, -1);
  return name;
}

function parseRecords(data: Uint8Array, joliet: boolean): RawRecord[] {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const out: RawRecord[] = [];
  let p = 0;
  while (p < data.length) {
    const len = data[p];
    if (len === 0) {
      // Padding to the next logical sector. Not an error, and not the end.
      const next = (Math.floor(p / SECTOR_ALIGN) + 1) * SECTOR_ALIGN;
      if (next <= p) break;
      p = next;
      continue;
    }
    if (p + len > data.length || len < 33) break;
    const extAttrSectors = data[p + 1];
    const lba = view.getUint32(p + 2, true) + extAttrSectors;
    const size = view.getUint32(p + 10, true);
    const flags = data[p + 25];
    const nameLen = data[p + 32];
    if (p + 33 + nameLen > data.length) break;
    const nameBytes = data.subarray(p + 33, p + 33 + nameLen);
    // The two special records: 0x00 is ".", 0x01 is "..".
    if (!(nameLen === 1 && (nameBytes[0] === 0 || nameBytes[0] === 1))) {
      out.push({ lba, size, flags, name: decodeName(nameBytes, joliet) });
    }
    p += len;
  }
  return out;
}

/** Guards against a directory cycle in a malformed image. */
const MAX_DEPTH = 16;

export async function readVolume(track: IsoTrack): Promise<IsoVolume> {
  const descriptors = await readDescriptors(track);
  const joliet = descriptors.find((d) => d.joliet);
  const primary = descriptors.find((d) => d.type === VD_PRIMARY);
  if (!primary) throw new IsoReadError('ISO image has no primary volume descriptor.');
  const chosen = joliet ?? primary;
  const useJoliet = Boolean(joliet);

  const v = new DataView(chosen.raw.buffer, chosen.raw.byteOffset, chosen.raw.byteLength);
  const blockSize = v.getUint16(128, true);
  if (blockSize !== USER_SIZE) {
    throw new IsoReadError(`Unsupported logical block size ${blockSize} (only 2048 is).`);
  }
  const volumeSectors = v.getUint32(80, true);
  const label = useJoliet
    ? decodeName(chosen.raw.subarray(40, 72), true).trim()
    : String.fromCharCode(...chosen.raw.subarray(40, 72)).trim();

  const rootLba = v.getUint32(156 + 2, true) + chosen.raw[156 + 1];
  const rootSize = v.getUint32(156 + 10, true);

  const entries: IsoEntry[] = [];
  const seen = new Set<number>();

  const walk = async (lba: number, size: number, prefix: string, depth: number): Promise<void> => {
    if (depth > MAX_DEPTH || seen.has(lba) || size === 0) return;
    seen.add(lba);
    const sectors = Math.ceil(size / USER_SIZE);
    const data = (await readSectors(track, lba, sectors)).subarray(0, size);
    const records = parseRecords(data, useJoliet);

    // Multi-extent files appear as consecutive records sharing a name, all but
    // the last carrying the continuation flag. Fold them into one entry.
    let pendingExtents: { lba: number; size: number }[] = [];
    let pendingName = '';

    for (const r of records) {
      const path = prefix ? `${prefix}/${r.name}` : r.name;
      if (r.flags & FLAG_DIRECTORY) {
        entries.push({ path, size: r.size, isDirectory: true, lba: r.lba });
        await walk(r.lba, r.size, path, depth + 1);
        continue;
      }
      if (r.flags & FLAG_MULTI_EXTENT) {
        pendingName = path;
        pendingExtents.push({ lba: r.lba, size: r.size });
        continue;
      }
      if (pendingExtents.length && pendingName === path) {
        const extents = [...pendingExtents, { lba: r.lba, size: r.size }];
        entries.push({
          path,
          size: extents.reduce((n, e) => n + e.size, 0),
          isDirectory: false,
          lba: extents[0].lba,
          extents,
        });
        pendingExtents = [];
        pendingName = '';
        continue;
      }
      pendingExtents = [];
      pendingName = '';
      entries.push({ path, size: r.size, isDirectory: false, lba: r.lba });
    }
  };

  await walk(rootLba, rootSize, '', 0);
  return { label, volumeSectors, joliet: useJoliet, entries };
}

// --- extent extraction -------------------------------------------------------

/**
 * Copies one extent out of a raw image, chunk by chunk.
 *
 * `onChunk` receives each de-framed run of user bytes. It is called with a fresh
 * `Uint8Array` every time, so the caller may keep it without copying.
 */
async function readExtent(
  track: IsoTrack,
  extent: { lba: number; size: number },
  onChunk: (chunk: Uint8Array<ArrayBuffer>) => void
): Promise<number> {
  const { geometry } = track;
  let sector = extent.lba;
  let remaining = extent.size;
  let copied = 0;

  while (remaining > 0) {
    const want = Math.min(STREAM_SECTORS, Math.ceil(remaining / USER_SIZE));
    const from = byteOffset(track, sector);
    const to = Math.min(from + want * geometry.rawSize, track.blob.size);
    const raw = new Uint8Array(await track.blob.slice(from, to).arrayBuffer());
    const whole = Math.floor(raw.length / geometry.rawSize);
    // A truncated image: stop cleanly rather than spinning. The size check in
    // extentBlob() turns this into a useful message.
    if (whole === 0) break;

    const take = Math.min(remaining, whole * USER_SIZE);
    const out = new Uint8Array(take);
    for (let i = 0; i < whole; i++) {
      const dst = i * USER_SIZE;
      if (dst >= take) break;
      const src = i * geometry.rawSize + geometry.headerOffset;
      out.set(raw.subarray(src, src + Math.min(USER_SIZE, take - dst)), dst);
    }
    onChunk(out);
    sector += whole;
    remaining -= take;
    copied += take;
  }
  return copied;
}

/**
 * A file's contents as a Blob.
 *
 * Cooked images are a straight `Blob.slice` — no copy, no heap at all. Raw
 * images are reassembled through the blob-parts sink described at the top of
 * this file, which keeps peak JS heap at one chunk regardless of file size.
 */
export async function extentBlob(track: IsoTrack, entry: IsoEntry): Promise<Blob> {
  const extents = entry.extents ?? [{ lba: entry.lba, size: entry.size }];
  if (isCooked(track.geometry) && extents.length === 1) {
    const from = byteOffset(track, extents[0].lba);
    return track.blob.slice(from, from + extents[0].size);
  }

  const parts: Blob[] = [];
  let copied = 0;
  for (const extent of extents) {
    // Wrapping each chunk in its own Blob is the load-bearing step: it moves the
    // bytes out of the JS heap the moment they exist.
    copied += await readExtent(track, extent, (chunk) => parts.push(new Blob([chunk])));
  }
  if (copied !== entry.size) {
    throw new IsoReadError(
      `${entry.path}: read ${copied} bytes but the directory says ${entry.size}. The image looks truncated.`
    );
  }
  return new Blob(parts);
}
